import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext/AuthContext";

const PRIMARY = "#009DB9";
const PRIMARY_DARK = "#007d94";
const PRIMARY_LIGHT = "rgba(0,157,185,0.12)";

export default function LoginPage() {
  const [employeeId, setEmployeeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    setError("");
    
    if (!employeeId.trim()) {
      setError("Please enter your Employee ID.");
      return;
    }
    
    setLoading(true);
    
    try {
      const success = await login(employeeId);
      if (success) {
        navigate("/dashboard");
      } else {
        setError("Employee ID not recognised. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.response?.data?.message || "Login failed. Please check your Employee ID and try again.");
      setLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center w-full min-h-screen"
      style={{ backgroundColor: "#e8f4f7" }}
    >
      {/* Main container - responsive width */}
      <div
        className="flex overflow-hidden shadow-2xl rounded-3xl"
        style={{ 
          width: "min(90%, 1200px)",
          height: "auto",
          minHeight: "500px",
          maxWidth: "1200px",
        }}
      >

        {/* LEFT PANEL - Hidden on mobile, visible on tablet/desktop */}
        <div
          className="hidden md:flex md:flex-col bg-white"
          style={{ width: "45%" }}
        >
          <div className="px-8 pt-8">
            <img
              src="/IZ-Logo.png"
              alt="IZSoftwares"
              className="object-contain w-16 h-16"
            />
          </div>

          <div className="flex items-center justify-center flex-1 px-6 pb-6">
            <img
              src="/Login Image.jpg"
              alt="Employee at workstation"
              className="object-contain w-full max-w-md select-none"
              draggable={false}
            />
          </div>
        </div>

        {/* RIGHT PANEL - Full width on mobile, 55% on desktop */}
        <div
          className="relative flex items-center justify-center flex-1"
          style={{ 
            backgroundColor: PRIMARY,
            minHeight: "500px",
          }}
        >
          {/* Logo - Top center on mobile, hidden on desktop */}
          <div className="absolute top-4 left-0 right-0 flex justify-center md:hidden">
            <img
              src="/IZ2-2.png"
              alt="IZSoftwares"
              className="object-contain w-14 h-14"
            />
          </div>

          {/* Decorative arcs - Desktop only */}
          <div className="absolute bottom-0 right-0 pointer-events-none hidden md:block">
            <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
              <circle cx="160" cy="160" r="65" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5"/>
              <circle cx="160" cy="160" r="95" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5"/>
              <circle cx="160" cy="160" r="125" stroke="rgba(255,255,255,0.07)" strokeWidth="1.5"/>
            </svg>
          </div>

          {/* Form Card */}
          <div
            className="relative bg-white rounded-2xl mx-4 md:mx-0"
            style={{
              width: "min(90%, 400px)",
              padding: "2rem 1.5rem",
              boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
            }}
          >
            {/* Title Section */}
            <div className="text-center">
              <h1 
                className="font-extrabold text-gray-900 mb-2"
                style={{ fontSize: "clamp(1.5rem, 4vw, 1.8rem)" }}
              >
                Employee Access
              </h1>

              <p className="text-sm text-gray-500 mb-6">
                Enter your Employee ID to access the internal portal
              </p>
            </div>

            {/* Employee ID Input */}
            <div className="relative mb-1">
              <label
                className="absolute -top-2.5 left-4 bg-white px-1 text-xs font-medium z-10 pointer-events-none transition-colors duration-200"
                style={{ color: focused ? PRIMARY : "#6b7280" }}
              >
                Employee ID
              </label>
              <div
                className="flex items-center border rounded-full px-4 py-3 transition-all duration-200"
                style={{
                  borderColor: focused ? PRIMARY : "#d1d5db",
                  boxShadow: focused ? `0 0 0 3px ${PRIMARY_LIGHT}` : "none",
                }}
              >
                <svg
                  className="flex-shrink-0 w-4 h-4 mr-3 transition-colors duration-200"
                  style={{ color: focused ? PRIMARY : "#9ca3af" }}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <input
                  type="text"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  className="flex-1 text-gray-700 placeholder-gray-400 bg-transparent outline-none"
                  style={{ fontSize: "16px" }}
                  autoFocus
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <p className="mt-2 text-xs text-red-500 text-center">{error}</p>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="mt-6 w-full rounded-full py-3 font-bold text-white tracking-wider transition-all duration-200 flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: PRIMARY }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = PRIMARY_DARK; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = PRIMARY; }}
            >
              {loading && (
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
              )}
              Access Portal
            </button>

            {/* Session info */}
            <p className="mt-4 text-xs text-center text-gray-400">
              Session remains active for 6 hours
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}